/**
 * RosterPanel — matches FOM "Manage Officer Roster" modal exactly.
 * Entry shape: { name, callSign, badgeNumber, radioNumber }
 * All category/CS/badge/radio logic ported from FOM desktop.html source.
 */
import { useState, useCallback } from 'react'
import { useAuthStore } from '../../stores/auth'
import { workerPostV1 } from '../../api/worker'
import { useSyncStore } from '../../stores/sync'

// ── PANEL CATEGORY → USERS.RANK MAPPING ──────────────────────────────────
// The Worker's rankToCategory() routes ranks back to panel categories on
// the next pull, so the rank string we store has to map back cleanly.
// Engineering / Management / FCPD use special rank tokens that the worker's
// rankToCategory() recognizes (added in an earlier fix).
function categoryToRank(category: string): string {
  if (category === 'Director of Security') return 'Director of Security'
  if (category === 'Assistant Director') return 'Assistant Director'
  if (category === 'Lieutenant') return 'Lieutenant'
  if (category === 'Sergeants') return 'Sergeant'
  if (category === 'Officers') return 'Officer'
  if (category === 'Dispatchers') return 'Dispatcher'
  if (category === 'Engineering') return 'Engineering'
  if (category === 'Management') return 'Management'
  if (category === 'Fairfax County Police') return 'FCPD'
  if (category === 'Developer') return 'Developer'
  return 'Officer'
}

async function pushRosterChange(payload: {
  action: 'upsert' | 'delete';
  callSign: string;
  name?: string;
  rank?: string;
  badgeNumber?: number | null;
  radioNumber?: number | null;
}, onError?: (msg: string) => void) {
  try {
    const result = await workerPostV1<{ success?: boolean; error?: string }>('/api/v1/roster/upsert', payload)
    if (result && result.success === false && result.error) {
      // Suppress badge/radio conflict errors — these are cross-pool (gold vs silver)
      // and do not affect local data which is already correct.
      const isBadgeConflict = result.error.toLowerCase().includes('badge') && result.error.toLowerCase().includes('assigned')
      const isRadioConflict = result.error.toLowerCase().includes('radio') && result.error.toLowerCase().includes('assigned')
      if (isBadgeConflict || isRadioConflict) {
        console.info('Roster: cross-pool assignment (expected):', result.error)
        return
      }
      console.warn('Roster sync rejected:', result.error)
      if (onError) onError(result.error)
    }
  } catch (err) {
    console.warn('Roster sync to D1 failed:', err)
  }
}

// ── FOM CONFIG (ported from desktop.html ce, te, O, Qe, ft) ─────────────

const CS_PREFIX: Record<string, string | string[]> = {
  Developer: 'DV-', Lieutenant: 'S-', 'Director of Security': 'S-',
  'Assistant Director': 'S-', Sergeants: 'S-', Dispatchers: 'D-',
  Officers: 'S-', 'Fairfax County Police': 'SC-',
  Management: ['M-', 'L-', 'O-', 'P-'], Engineering: 'E-',
}

const NO_BADGE_CATS = ['Fairfax County Police', 'Management', 'Engineering']
// Engineering, Management, and FCPD personnel can be issued radios from the same
// pool as security officers (e.g. radios 92, 93, 47, 50, 105, 108, 113, 116, 118
// in the FOM data), so they need editable radio fields. Only badges remain
// restricted to security categories.
const NO_RADIO_CATS: string[] = []

function hasBadge(cat: string) { return !NO_BADGE_CATS.includes(cat) }
function hasRadio(cat: string) { return !NO_RADIO_CATS.includes(cat) }

function csNumbers(cat: string): string[] {
  if (cat === 'Developer') return ['01', '02', '03']
  if (['Lieutenant', 'Director of Security', 'Assistant Director'].includes(cat)) return ['01', '02', '03']
  if (cat === 'Sergeants') return ['04', '05', '06', '07', '08']
  if (['Officers', 'Dispatchers'].includes(cat)) return Array.from({ length: 35 }, (_, i) => String(i + 1).padStart(2, '0'))
  if (cat === 'Fairfax County Police') return [...Array.from({ length: 10 }, (_, i) => String(i + 1).padStart(2, '0')), '81', '82']
  return Array.from({ length: 10 }, (_, i) => String(i + 1).padStart(2, '0'))
}

function badgeNumbers(cat: string): number[] {
  if (['Lieutenant', 'Director of Security', 'Assistant Director', 'Sergeants', 'Developer'].includes(cat))
    return Array.from({ length: 8 },  (_, i) => i + 1)   // Supervisor badges: 1-8
  if (['Officers', 'Dispatchers'].includes(cat))
    return Array.from({ length: 30 }, (_, i) => i + 1)   // Officer badges: 1-30
  return Array.from({ length: 10 }, (_, i) => i + 1)
}

const DEFAULT_CATEGORIES = ['Lieutenant', 'Director of Security', 'Assistant Director', 'Sergeants', 'Dispatchers', 'Officers', 'Fairfax County Police', 'Management', 'Engineering', 'Developer']
const DEFAULT_ROSTER: Record<string, RosterEntry[]> = Object.fromEntries(DEFAULT_CATEGORIES.map(c => [c, []]))

// ── TYPES ─────────────────────────────────────────────────────────────────

interface RosterEntry {
  name: string
  callSign: string
  badgeNumber: number | null
  radioNumber: number | null
}

// ── Roster equipment persistence ─────────────────────────────────────────
// Badge and radio assignments are stored in a SEPARATE key from fom_roster
// so they survive sync pulls, resets, and any fom_roster rewrites.
// This key is NEVER in LS_KEYS and is never touched by sync logic.
const ROSTER_EQ_KEY = 'fom_roster_badge_radio'

function loadRosterEquipment(): Record<string, { badgeNumber: number | null; radioNumber: number | null }> {
  try { return JSON.parse(localStorage.getItem(ROSTER_EQ_KEY) ?? '{}') } catch { return {} }
}

function saveRosterEquipment(callSign: string, badgeNumber: number | null, radioNumber: number | null) {
  try {
    const map = loadRosterEquipment()
    map[callSign.toUpperCase()] = { badgeNumber, radioNumber }
    localStorage.setItem(ROSTER_EQ_KEY, JSON.stringify(map))
  } catch { /* silent */ }
}

function mergeEquipmentIntoRoster(roster: Record<string, RosterEntry[]>): Record<string, RosterEntry[]> {
  const eq = loadRosterEquipment()
  if (Object.keys(eq).length === 0) return roster
  const merged: Record<string, RosterEntry[]> = {}
  for (const [cat, entries] of Object.entries(roster)) {
    merged[cat] = (entries as RosterEntry[]).map(e => {
      const saved = e.callSign ? eq[e.callSign.toUpperCase()] : undefined
      if (!saved) return e
      return {
        ...e,
        badgeNumber: saved.badgeNumber ?? e.badgeNumber,
        radioNumber: saved.radioNumber ?? e.radioNumber,
      }
    })
  }
  return merged
}

// ── STORAGE HELPERS ───────────────────────────────────────────────────────

function loadRoster(): Record<string, RosterEntry[]> {
  try {
    const raw = localStorage.getItem('fom_roster')
    if (!raw) return mergeEquipmentIntoRoster({ ...DEFAULT_ROSTER })
    const parsed = JSON.parse(raw)
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return mergeEquipmentIntoRoster({ ...DEFAULT_ROSTER })
    const result: Record<string, RosterEntry[]> = { ...DEFAULT_ROSTER }
    for (const [cat, entries] of Object.entries(parsed)) {
      result[cat] = entries as RosterEntry[]
    }
    // Always overlay the durable badge/radio store on top of whatever fom_roster has
    return mergeEquipmentIntoRoster(result)
  } catch { return mergeEquipmentIntoRoster({ ...DEFAULT_ROSTER }) }
}

function saveRoster(roster: Record<string, RosterEntry[]>) {
  try {
    localStorage.setItem('fom_roster', JSON.stringify(roster))
    localStorage.setItem('fom_roster_backup', JSON.stringify(roster))
    localStorage.setItem('fom_roster_backup_ts', new Date().toISOString())
    // Fire a dedicated roster event — NOT pull-complete, which triggers too many side effects
    window.dispatchEvent(new CustomEvent('castellan:roster-updated'))
  } catch { /* silent */ }
}

function flatRoster(roster: Record<string, RosterEntry[]>) {
  return Object.values(roster).flat()
}

const SUP_BADGE_CATS = ['Lieutenant', 'Director of Security', 'Assistant Director', 'Sergeants', 'Developer']
const OFC_BADGE_CATS = ['Officers', 'Dispatchers']

function takenBadges(roster: Record<string, RosterEntry[]>, cat: string): number[] {
  // Supervisor and officer badges are separate physical pools — same number
  // can exist in both pools without conflict. Check only within the same pool.
  const isSupCat = SUP_BADGE_CATS.includes(cat)
  const isOfcCat = OFC_BADGE_CATS.includes(cat)
  const catsToCheck = isSupCat ? SUP_BADGE_CATS : isOfcCat ? OFC_BADGE_CATS : Object.keys(roster)
  return catsToCheck
    .flatMap(c => (roster[c] ?? []).map(e => e.badgeNumber).filter((n): n is number => n != null))
}

function takenRadios(roster: Record<string, RosterEntry[]>): Array<number | string> {
  return Object.values(roster)
    .flatMap(entries => entries.map(e => e.radioNumber).filter((n): n is number => n != null))
}

function radioIsTaken(radiosInUse: Array<number | string>, n: number | string): boolean {
  return radiosInUse.some(r => String(r) === String(n))
}

function allRadioIds(): Array<number | string> {
  // Full FOM radio inventory — all IDs that can be assigned to roster officers
  const numeric: number[] = []
  // Officer pool: 102-117
  for (let i = 102; i <= 117; i++) numeric.push(i)
  // Supervisor / other: 92-99
  for (let i = 92; i <= 99; i++) numeric.push(i)
  // Engineering / spare: 47, 50, 51, 52, 105, 108, 113, 116, 118
  for (const n of [47, 50, 51, 52, 105, 108, 113, 116, 118]) numeric.push(n)
  // Available/spare pool: 10, 28
  for (const n of [10, 28]) numeric.push(n)
  // SGT radios (string IDs)
  const str: string[] = ['SGT-100', 'SGT-101', 'R-23']
  return [...numeric.sort((a, b) => a - b), ...str]
}

// ── STYLES ────────────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  background: '#0f1a2e', border: '1px solid #1e3a5f', borderRadius: 6,
  color: '#e2e8f0', fontSize: 12, padding: '6px 10px', outline: 'none',
  boxSizing: 'border-box',
}

const btn = (color: string): React.CSSProperties => ({
  padding: '6px 14px', background: color, border: 'none',
  borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 700,
  cursor: 'pointer', whiteSpace: 'nowrap',
})

// ── MAIN COMPONENT ────────────────────────────────────────────────────────

interface RosterPanelProps {
  onClose?: () => void
}

export default function RosterPanel({ onClose }: RosterPanelProps = {}) {
  const [roster, setRoster] = useState<Record<string, RosterEntry[]>>(loadRoster)
  const [msg, setMsg] = useState('')

  const [addCat, setAddCat] = useState('Officers')
  const [addName, setAddName] = useState('')
  const [addCS, setAddCS] = useState('')
  const [addBadge, setAddBadge] = useState('')
  const [addRadio, setAddRadio] = useState('')
  const [addMgmtPrefix, setAddMgmtPrefix] = useState('M-')

  const [editCS, setEditCS] = useState<{ cat: string; idx: number; val: string; prefix: string } | null>(null)
  const [editBadge, setEditBadge] = useState<{ cat: string; idx: number; val: string } | null>(null)
  const [editRadio, setEditRadio] = useState<{ cat: string; idx: number; val: string } | null>(null)
  const [editName, setEditName] = useState<{ cat: string; idx: number; val: string } | null>(null)

  const update = useCallback((newRoster: Record<string, RosterEntry[]>) => {
    setRoster(newRoster)
    saveRoster(newRoster)
  }, [])

  const flat = flatRoster(roster)
  const total = flat.length
  const radiosInUse = takenRadios(roster)
  const allRadios = allRadioIds()

  const [pulling, setPulling] = useState(false)
  const forcePull = useSyncStore(s => s.forcePull)

  // Reload roster from localStorage whenever a pull completes
  const syncPullCount = useSyncStore(s => s.pullCount)
  const [prevPullCount, setPrevPullCount] = useState(syncPullCount)
  if (syncPullCount !== prevPullCount) {
    setPrevPullCount(syncPullCount)
    setRoster(loadRoster())
  }

  async function pullFromD1() {
    setPulling(true)
    await forcePull()
    setRoster(loadRoster())
    flash('Roster refreshed from D1 ✓')
    setPulling(false)
  }

  function flash(m: string) { setMsg(m); setTimeout(() => setMsg(''), 4000) }

  function addOfficer() {
    if (!addName.trim() || !addCS) { flash('Name and call sign number are required.'); return }
    const name = addName.trim()
    if (flat.some(e => e.name === name)) { flash('This officer already exists in the roster.'); return }
    const prefix = addCat === 'Management' ? addMgmtPrefix : (Array.isArray(CS_PREFIX[addCat]) ? 'M-' : CS_PREFIX[addCat] as string)
    const callSign = `${prefix}${addCS}`
    const badge = addBadge ? parseInt(addBadge) : null
    const radio = addRadio ? parseInt(addRadio) : null
    const newRoster = { ...roster, [addCat]: [...(roster[addCat] || []), { name, callSign, badgeNumber: badge, radioNumber: radio }] }
    update(newRoster)
    pushRosterChange({ action: 'upsert', callSign, name, rank: categoryToRank(addCat), badgeNumber: badge, radioNumber: radio })
    setAddName(''); setAddCS(''); setAddBadge(''); setAddRadio('')
    flash(`${name} added to ${addCat}`)
  }

  function removeOfficer(cat: string, idx: number) {
    const entry = roster[cat][idx]
    if (!confirm(`Remove ${entry.name} from roster?`)) return
    const newCat = roster[cat].filter((_, i) => i !== idx)
    update({ ...roster, [cat]: newCat })
    pushRosterChange({ action: 'delete', callSign: entry.callSign })
    flash(`${entry.name} removed`)
  }

  function saveCS(cat: string, idx: number, val: string, prefix: string) {
    const oldEntry = roster[cat][idx]
    const callSign = val ? `${prefix}${val}` : ''
    const updated = roster[cat].map((e, i) => i === idx ? { ...e, callSign } : e)
    update({ ...roster, [cat]: updated })
    // Call sign is the primary key in users — a CS change is a delete + insert
    if (oldEntry.callSign && oldEntry.callSign !== callSign) {
      pushRosterChange({ action: 'delete', callSign: oldEntry.callSign })
    }
    if (callSign) {
      pushRosterChange({
        action: 'upsert', callSign, name: oldEntry.name,
        rank: categoryToRank(cat),
        badgeNumber: oldEntry.badgeNumber, radioNumber: oldEntry.radioNumber,
      })
    }
    setEditCS(null)
  }

  function saveBadge(cat: string, idx: number, val: string) {
    const entry = roster[cat][idx]
    const badge = val ? parseInt(val) : null
    const updated = roster[cat].map((e, i) => i === idx ? { ...e, badgeNumber: badge } : e)
    update({ ...roster, [cat]: updated })
    // Write to durable badge/radio store — survives fom_roster rewrites
    if (entry.callSign) {
      saveRosterEquipment(entry.callSign, badge, entry.radioNumber)
      pushRosterChange({
        action: 'upsert', callSign: entry.callSign, name: entry.name,
        rank: categoryToRank(cat),
        badgeNumber: badge, radioNumber: entry.radioNumber,
      }, flash)
    }
    setEditBadge(null)
  }

  function saveRadio(cat: string, idx: number, val: string) {
    const entry = roster[cat][idx]
    const radio = val ? parseInt(val) : null
    const updated = roster[cat].map((e, i) => i === idx ? { ...e, radioNumber: radio } : e)
    update({ ...roster, [cat]: updated })
    // Write to durable badge/radio store — survives fom_roster rewrites
    if (entry.callSign) {
      saveRosterEquipment(entry.callSign, entry.badgeNumber, radio)
      pushRosterChange({
        action: 'upsert', callSign: entry.callSign, name: entry.name,
        rank: categoryToRank(cat),
        badgeNumber: entry.badgeNumber, radioNumber: radio,
      }, flash)
    }
    setEditRadio(null)
  }

  function saveName(cat: string, idx: number, val: string) {
    const entry = roster[cat][idx]
    const updated = roster[cat].map((e, i) => i === idx ? { ...e, name: val } : e)
    update({ ...roster, [cat]: updated })
    if (entry.callSign && val) {
      pushRosterChange({
        action: 'upsert', callSign: entry.callSign, name: val,
        rank: categoryToRank(cat),
        badgeNumber: entry.badgeNumber, radioNumber: entry.radioNumber,
      }, flash)
    }
    setEditName(null)
  }

  function resetRoster() {
    if (!confirm('⚠️ Clear all names from the roster? This cannot be undone.')) return
    update({ ...DEFAULT_ROSTER })
    flash('Roster cleared.')
  }

  function restoreBackup() {
    try {
      const bk = localStorage.getItem('fom_roster_backup')
      if (!bk) { alert('No backup found.'); return }
      const br = JSON.parse(bk) as Record<string, RosterEntry[]>
      const ts = localStorage.getItem('fom_roster_backup_ts') || 'unknown'
      const names = Object.values(br).flat()
      if (!confirm(`Restore ${names.length} officers from backup?\n\nBackup from: ${new Date(ts).toLocaleString()}`)) return
      update(br)
      flash(`Roster restored from backup (${names.length} officers)`)
    } catch (e) { alert('Backup restore failed: ' + (e as Error).message) }
  }

  const csPrefix = addCat === 'Management' ? addMgmtPrefix : (Array.isArray(CS_PREFIX[addCat]) ? addMgmtPrefix : CS_PREFIX[addCat] as string ?? '')
  const badgeEligible = hasBadge(addCat)
  const radioEligible = hasRadio(addCat)
  const takenBadgesForCat = takenBadges(roster, addCat)

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget && onClose) onClose() }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 24,
      }}
    >
    <div style={{
      background: '#162033',
      border: '1px solid #c9a84c',
      borderRadius: 14,
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: 1100,
      maxHeight: '85vh',
      overflow: 'hidden',
      boxShadow: '0 25px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.2)',
    }}>

      {/* Header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #1e3a5f', flexShrink: 0, background: 'linear-gradient(135deg,#1e2d45,#162033)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div>
            <div style={{ color: '#c9a84c', fontSize: 14, fontWeight: 700, letterSpacing: 0.3 }}>👥 Manage Officer Roster</div>
            <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{total} officers in {Object.keys(roster).filter(c => roster[c].length > 0).length} categories</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={restoreBackup} style={btn('#059669')}>Restore Backup</button>
            <button onClick={pullFromD1} disabled={pulling} style={{ ...btn('#0ea5e9'), opacity: pulling ? 0.6 : 1 }}>
              {pulling ? 'Pulling…' : '⬇ Pull from D1'}
            </button>
            <button onClick={resetRoster} style={btn('#dc2626')}>Reset Roster</button>
            {onClose && (
              <button
                onClick={onClose}
                title="Close"
                aria-label="Close"
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(201,168,76,0.18)'
                  e.currentTarget.style.color = '#c9a84c'
                  e.currentTarget.style.borderColor = 'rgba(201,168,76,0.6)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent'
                  e.currentTarget.style.color = '#94a3b8'
                  e.currentTarget.style.borderColor = 'rgba(148,163,184,0.3)'
                }}
                style={{
                  marginLeft: 4,
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'transparent',
                  border: '1px solid rgba(148,163,184,0.3)',
                  color: '#94a3b8',
                  fontSize: 18,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                  transition: 'background 0.15s, color 0.15s, border-color 0.15s',
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
        {msg && (
          <div style={{ marginTop: 8, padding: '6px 12px', borderRadius: 6, fontSize: 12, background: msg.includes('required') || msg.includes('already') ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)', color: msg.includes('required') || msg.includes('already') ? '#ef4444' : '#22c55e', border: `1px solid ${msg.includes('required') || msg.includes('already') ? 'rgba(239,68,68,0.3)' : 'rgba(34,197,94,0.3)'}` }}>
            {msg}
          </div>
        )}
      </div>

      {/* Quick Add */}
      <div style={{ padding: '10px 16px', background: 'rgba(30,58,95,0.3)', borderBottom: '1px solid #1e3a5f', flexShrink: 0 }}>
        <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>Quick Add Officer</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <select value={addCat} onChange={e => { setAddCat(e.target.value); setAddCS(''); setAddBadge(''); setAddRadio('') }} style={{ ...inp, width: 170 }}>
            {Object.keys(roster).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {addCat === 'Management' && (
            <select value={addMgmtPrefix} onChange={e => setAddMgmtPrefix(e.target.value)} style={{ ...inp, width: 60 }}>
              {['M-', 'L-', 'O-', 'P-'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="Name (Last, First)"
            style={{ ...inp, flex: 1, minWidth: 140 }} onKeyDown={e => e.key === 'Enter' && addOfficer()} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{csPrefix}</span>
            <select value={addCS} onChange={e => setAddCS(e.target.value)} style={{ ...inp, width: 64 }}>
              <option value="">CS#</option>
              {csNumbers(addCat).map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {badgeEligible ? (
            <select value={addBadge} onChange={e => setAddBadge(e.target.value)} style={{ ...inp, width: 95 }}>
              <option value="">Badge#</option>
              {badgeNumbers(addCat).map(n => {
                const taken = takenBadgesForCat.includes(n)
                return <option key={n} value={n} disabled={taken} style={taken ? { color: '#475569' } : {}}>
                  Badge {String(n).padStart(2, '0')}{taken ? ' ✓' : ''}
                </option>
              })}
            </select>
          ) : <div style={{ ...inp, width: 95, color: '#475569', textAlign: 'center' }}>No Badge</div>}
          {radioEligible ? (
            <select value={addRadio} onChange={e => setAddRadio(e.target.value)} style={{ ...inp, width: 90 }}>
              <option value="">Radio#</option>
              {allRadios.map(n => {
                const taken = radioIsTaken(radiosInUse, n)
                const label = typeof n === 'string' ? n : `R-${n}`
                return <option key={n} value={n} disabled={taken} style={taken ? { color: '#475569' } : {}}>
                  {label}{taken ? ' ✓' : ''}
                </option>
              })}
            </select>
          ) : <div style={{ ...inp, width: 90, color: '#475569', textAlign: 'center' }}>No Radio</div>}
          <button onClick={addOfficer} disabled={!addName.trim() || !addCS}
            style={{ ...btn('#22c55e'), opacity: !addName.trim() || !addCS ? 0.5 : 1 }}>+ Add</button>
        </div>
      </div>

      {/* Roster list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {Object.entries(roster).map(([cat, entries]) => {
          const prefix = cat === 'Management' ? 'M-' : (Array.isArray(CS_PREFIX[cat]) ? 'M-' : CS_PREFIX[cat] as string ?? '')
          const bEligible = hasBadge(cat)
          const rEligible = hasRadio(cat)
          const takenB = takenBadges(roster, cat)

          return (
            <div key={cat} style={{ marginBottom: 12, border: '1px solid rgba(30,58,95,0.8)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ background: 'rgba(30,58,95,0.5)', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#c9a84c' }}>{cat}</span>
                <span style={{ fontSize: 10, background: '#1e3a5f', color: '#64748b', padding: '1px 6px', borderRadius: 10 }}>{entries.length}</span>
                {!bEligible && !rEligible && <span style={{ fontSize: 10, color: '#475569', fontStyle: 'italic' }}>No badges/radios</span>}
              </div>
              <div>
                {entries.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#475569', fontStyle: 'italic', padding: '8px 12px' }}>No officers in this category</div>
                ) : entries.map((entry, idx) => {
                  const isEditingCS = editCS?.cat === cat && editCS.idx === idx
                  const isEditingBadge = editBadge?.cat === cat && editBadge.idx === idx
                  const isEditingRadio = editRadio?.cat === cat && editRadio.idx === idx
                  const isEditingName = editName?.cat === cat && editName.idx === idx
                  const entryPrefix = cat === 'Management' ? (entry.callSign.match(/^[A-Z]+-/)?.[0] || 'M-') : prefix

                  return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid rgba(30,58,95,0.4)', flexWrap: 'wrap', gap: 6 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', flex: 1 }}>

                        {isEditingName ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input value={editName.val} onChange={e => setEditName({ ...editName, val: e.target.value })} style={{ ...inp, width: 160 }} autoFocus />
                            <button onClick={() => saveName(cat, idx, editName.val)} style={{ color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer' }}>✓</button>
                            <button onClick={() => setEditName(null)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                          </div>
                        ) : (
                          <span onClick={() => setEditName({ cat, idx, val: entry.name })} style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', cursor: 'pointer' }} title="Click to edit">{entry.name}</span>
                        )}

                        {isEditingCS ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {cat === 'Management' && <select value={editCS.prefix} onChange={e => setEditCS({ ...editCS, prefix: e.target.value })} style={{ ...inp, width: 55, fontSize: 11 }}>{['M-', 'L-', 'O-', 'P-'].map(p => <option key={p} value={p}>{p}</option>)}</select>}
                            {cat !== 'Management' && <span style={{ fontSize: 11, color: '#94a3b8' }}>{entryPrefix}</span>}
                            <select value={editCS.val} onChange={e => setEditCS({ ...editCS, val: e.target.value })} style={{ ...inp, width: 60, fontSize: 11 }}>
                              <option value="">--</option>
                              {csNumbers(cat).map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <button onClick={() => saveCS(cat, idx, editCS.val, editCS.prefix)} style={{ color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer' }}>✓</button>
                            <button onClick={() => setEditCS(null)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                          </div>
                        ) : (
                          <button onClick={() => setEditCS({ cat, idx, val: entry.callSign.replace(entryPrefix, ''), prefix: entryPrefix })}
                            style={{ fontSize: 11, padding: '2px 8px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 4, color: '#c9a84c', cursor: 'pointer', fontWeight: 700 }}>
                            {entry.callSign || 'Set CS'}
                          </button>
                        )}

                        {bEligible && (isEditingBadge ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <select value={editBadge.val} onChange={e => setEditBadge({ ...editBadge, val: e.target.value })} style={{ ...inp, width: 100, fontSize: 11 }}>
                              <option value="">None</option>
                              {badgeNumbers(cat).map(n => { const t = takenB.includes(n) && roster[cat][idx].badgeNumber !== n; return <option key={n} value={n} disabled={t} style={t ? { color: '#475569' } : {}}>Badge {String(n).padStart(2, '0')}{t ? ' ✓' : ''}</option> })}
                            </select>
                            <button onClick={() => saveBadge(cat, idx, editBadge.val)} style={{ color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer' }}>✓</button>
                            <button onClick={() => setEditBadge(null)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                          </div>
                        ) : (
                          <button onClick={() => setEditBadge({ cat, idx, val: String(entry.badgeNumber || '') })}
                            style={{ fontSize: 11, padding: '2px 8px', background: entry.badgeNumber ? 'rgba(59,130,246,0.15)' : 'rgba(30,58,95,0.3)', border: `1px solid ${entry.badgeNumber ? 'rgba(59,130,246,0.3)' : 'rgba(30,58,95,0.5)'}`, borderRadius: 4, color: entry.badgeNumber ? '#60a5fa' : '#475569', cursor: 'pointer' }}>
                            {entry.badgeNumber ? `Badge ${String(entry.badgeNumber).padStart(2, '0')}` : 'Set Badge'}
                          </button>
                        ))}
                        {!bEligible && <span style={{ fontSize: 11, color: '#475569', padding: '2px 8px', background: 'rgba(30,58,95,0.2)', borderRadius: 4 }}>No Badge</span>}

                        {rEligible && (isEditingRadio ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <select value={editRadio.val} onChange={e => setEditRadio({ ...editRadio, val: e.target.value })} style={{ ...inp, width: 90, fontSize: 11 }}>
                              <option value="">None</option>
                              {allRadios.map(n => { const t = radioIsTaken(radiosInUse, n) && roster[cat][idx].radioNumber !== n; const label = typeof n === 'string' ? n : `R-${n}`; return <option key={n} value={n} disabled={t} style={t ? { color: '#475569' } : {}}>{label}{t ? ' ✓' : ''}</option> })}
                            </select>
                            <button onClick={() => saveRadio(cat, idx, editRadio.val)} style={{ color: '#22c55e', background: 'none', border: 'none', cursor: 'pointer' }}>✓</button>
                            <button onClick={() => setEditRadio(null)} style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                          </div>
                        ) : (
                          <button onClick={() => setEditRadio({ cat, idx, val: String(entry.radioNumber || '') })}
                            style={{ fontSize: 11, padding: '2px 8px', background: entry.radioNumber ? 'rgba(139,92,246,0.15)' : 'rgba(30,58,95,0.3)', border: `1px solid ${entry.radioNumber ? 'rgba(139,92,246,0.3)' : 'rgba(30,58,95,0.5)'}`, borderRadius: 4, color: entry.radioNumber ? '#a78bfa' : '#475569', cursor: 'pointer' }}>
                            {entry.radioNumber ? `Radio ${entry.radioNumber}` : 'Set Radio'}
                          </button>
                        ))}
                        {!rEligible && <span style={{ fontSize: 11, color: '#475569', padding: '2px 8px', background: 'rgba(30,58,95,0.2)', borderRadius: 4 }}>No Radio</span>}

                      </div>
                      <button onClick={() => removeOfficer(cat, idx)} style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 5, color: '#ef4444', fontSize: 11, padding: '3px 8px', cursor: 'pointer' }}>✕</button>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
    </div>
  )
}
